import { sleep } from '@last/core'

import SocketFactory from '@/drivers/socket'

import type { PrinterConfig, PrinterDriver } from '../types'
import { EscPosEncoder } from './esc-pos-encoder'

export default class EscPos implements PrinterDriver {
  private config: PrinterConfig & { matrixMode?: boolean }

  constructor(config: PrinterConfig & { matrixMode?: boolean }) {
    this.config = config
  }

  printImage(canvas: HTMLCanvasElement) {
    return new Promise<void>((resolve, reject) => {
      const rejectMiddleware = (error: unknown) => {
        reject(error)
      }
      const socket = SocketFactory.getSocket()
      const config = this.config
      socket.open(
        this.config.ip,
        9100,
        async () => {
          const width = config.matrixMode ? 200 : 504
          const ratio = width / canvas.width
          const height = Math.round((canvas.height * ratio) / 8) * 8

          const encoder = new EscPosEncoder()
          encoder.align('center')
          if (config.matrixMode) {
            encoder.imageEsc(canvas, width, height)
          } else {
            encoder.image(canvas, width, height)
          }

          const result = encoder.newline(5).cut('partial').encode()

          socket.write(
            result,
            () => {
              socket.shutdownWrite(() => {
                resolve()
              }, rejectMiddleware)
            },
            rejectMiddleware
          )
        },
        rejectMiddleware
      )
    })
  }

  openCashDrawer() {
    return new Promise<void>((resolve, reject) => {
      const socket = SocketFactory.getSocket()
      socket.open(
        this.config.ip,
        9100,
        () => {
          const encoder = new EscPosEncoder()
          const result = encoder.openCashDrawer().encode()

          socket.write(
            result,
            () => {
              socket.shutdownWrite(() => {
                resolve()
              }, reject)
            },
            reject
          )
        },
        reject
      )
    })
  }

  /**
   * Gets printer information using ESC/POS "GS I" commands
   *
   * This method sends four sequential "GS I" commands to request printer information:
   * - GS I 65 (0x41): Firmware version
   * - GS I 66 (0x42): Manufacturer name
   * - GS I 67 (0x43): Model name
   * - GS I 68 (0x44): Serial number
   *
   * Commands are sent individually with 100ms delays between them, as some printers
   * need time to process each command before receiving the next one.
   *
   * Response format: Each response consists of a header byte followed by the data
   * and terminated with a null byte (0x00). The responses are parsed by splitting
   * on null bytes and filtering out non-printable characters.
   *
   * https://download4.epson.biz/sec_pubs/pos/reference_en/escpos/gs_ci.html
   *
   * @returns Promise with printer information or null if no response received
   */
  getInfo() {
    return new Promise<{
      firmwareVersion?: string
      makerName?: string
      modelName?: string
      serialNumber?: string
    } | null>((resolve, reject) => {
      const socket = SocketFactory.getSocket()
      let receivedData = Buffer.alloc(0)

      socket.open(
        this.config.ip,
        9100,
        async () => {
          const commands = [0x41, 0x42, 0x43, 0x44]
          for (const cmd of commands) {
            const encoder = new EscPosEncoder()
            encoder.raw([0x1d, 0x49, cmd])
            await new Promise<void>((resolveWrite, rejectWrite) => {
              socket.write(encoder.encode(), resolveWrite, rejectWrite)
            })
            await sleep(100)
          }
        },
        reject
      )

      socket.onData(data => {
        if (data && data.length > 0) {
          receivedData = Buffer.concat([receivedData, Buffer.from(data)])
        }
      })

      setTimeout(() => {
        socket.shutdownWrite(() => {
          if (receivedData.length === 0) {
            resolve(null)
            return
          }

          const responses = receivedData
            .toString('utf-8')
            .split(/[\0_]/)
            .filter(str => str.length > 0)

          const info = {
            firmwareVersion: responses[0],
            makerName: responses[1],
            modelName: responses[2],
            serialNumber: responses[3]
          }

          resolve(info)
        }, reject)
      }, 3_000)
    })
  }
}
